import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { getFormData } from 'src/content/Documents/utils/utils';
import { APPS_NAV_LINK } from 'src/content/MiniExperts/constants';
import { setPendingUploadUUID } from 'src/content/MiniExperts/SmartMeet/utils/pendingUpload';
import {
  clampMeetingName,
  getFormattedTime,
  getNewMeetingName,
  resolveProcessingRoute
} from 'src/content/MiniExperts/SmartMeet/utils/utils';
import useMutation from 'src/hooks/useMutation';
import { navigationBypass } from 'src/hooks/usePreventNavigation';
import { updateSmartMeetCount } from 'src/redux/slices/data';
import {
  RecordingProgress,
  setMeetingData,
  setProgress
} from 'src/redux/slices/smartMeet';
import { setAlert } from 'src/redux/slices/snackbar';
import { useDispatch, useSelector } from 'src/redux/store';
import { useUploadDocumentsMutation } from 'src/services/api';
import { ALERT, UPLOAD_TYPE } from 'src/types/enum';
import useAnalytics from 'src/hooks/useAnalytics';
import {
  SMART_MEET_MEETING_RECORDED,
  SMART_MEET_MEETING_UPLOADED
} from 'src/utils/analyticsUtils';
import { ErrorContext, ErrorMessages } from 'src/utils/errorMappings';
import logger from 'src/utils/logger';
import { deleteMeetingData } from 'src/utils/recordingDB';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import { v4 as uuidV4 } from 'uuid';

export const useCreateMeetHandler = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const collectAnalytics = useAnalytics();
  const { notes, seconds, draftMeeting } = useSelector(
    (state) => state.smartMeet
  );

  const [uploadDocuments] = useMutation({
    api: useUploadDocumentsMutation,
    errorContext: ErrorContext.SMART_MEET
  });

  const uploadMeetingAudio = async (
    audioBlob: Blob,
    params?: {
      duration?: number;
      fileName?: string;
      meetingId?: number;
      extraArgs?: Record<string, string>;
    }
  ) => {
    if (!audioBlob) {
      logger.warn('No audio blob to upload');
      return null;
    }
    const { duration = seconds, fileName, meetingId, extraArgs } = params ?? {};
    const meetingName = clampMeetingName(fileName ?? getNewMeetingName());
    try {
      const transactionId = uuidV4();

      let fileToUpload: File;

      const ext = audioBlob.type?.split('/')[1] || 'wav';
      const baseName = meetingName.replace(/\.[^/.]+$/, '');
      const safeName = `${baseName}.${ext === 'mpeg' ? 'mp3' : ext}`;

      if (
        audioBlob.type === 'audio/wav' ||
        audioBlob.type === 'audio/wave' ||
        meetingName.endsWith('.wav')
      ) {
        try {
          fileToUpload = new File([audioBlob], safeName, {
            type: audioBlob.type || 'audio/wav'
          });
        } catch {
          fileToUpload = new File(
            [audioBlob],
            meetingName.endsWith('.wav') ? meetingName : `${meetingName}.wav`,
            { type: audioBlob.type || 'audio/wav' }
          );
        }
      } else {
        fileToUpload = new File([audioBlob], safeName, {
          type: audioBlob.type || 'audio/wav'
        });
      }

      dispatch(
        setProgress({
          status_code: RecordingProgress.UploadingAudio
        })
      );
      navigate(
        meetingId
          ? `${APPS_NAV_LINK.SMART_MEET}/${meetingId}`
          : `${APPS_NAV_LINK.SMART_MEET}/processing`
      );

      const formData = (await getFormData(
        [fileToUpload],
        [],
        transactionId,
        UPLOAD_TYPE.CHAT_FILE,
        undefined,
        true,
        true,
        true,
        {
          sm_args: JSON.stringify({
            notes: JSON.stringify({ items: notes }),
            duration: getFormattedTime(duration),
            ...(meetingId ? { meeting_id: meetingId } : { name: meetingName }),
            ...extraArgs
          })
        }
      )) as FormData;

      if (extraArgs?.uuid) {
        setPendingUploadUUID(extraArgs.uuid);
      }

      await uploadDocuments({
        params: {
          params: {
            formData: formData
          }
        },
        fallbackMsg: T.uploadDocFailed,
        successCallback: (_) => {
          setPendingUploadUUID(null);

          if (meetingId && draftMeeting?.tenant_id) {
            deleteMeetingData(`${meetingId}-${draftMeeting.tenant_id}`).catch(
              (err) => {
                logger.error('Error deleting meeting data:', err);
              }
            );
          }

          const meetingData = _?.data?.smart_meet?.[0];
          const ConsumedSmartMeetSeconds =
            _?.data?.total_limit_seconds ?? meetingData?.total_limit_seconds;
          if (
            ConsumedSmartMeetSeconds !== undefined &&
            ConsumedSmartMeetSeconds !== null
          ) {
            dispatch(updateSmartMeetCount(ConsumedSmartMeetSeconds));
          }
          if (meetingData?.id) {
            collectAnalytics(
              meetingId ? SMART_MEET_MEETING_UPLOADED : SMART_MEET_MEETING_RECORDED,
              {
                meeting_id: meetingData.id,
                duration_seconds: duration,
                source: meetingId ? 'upload' : 'recording',
                ...(!meetingId && { file_name: fileName })
              }
            );
          }
          dispatch(setMeetingData(meetingData));
          dispatch(
            setProgress({
              state: meetingData?.state,
              status_code: meetingData?.status_code,
              status_details: meetingData?.status_details
            })
          );
          resolveProcessingRoute(meetingData?.id);
        },
        errorCallback: (_) => {
          setPendingUploadUUID(null);
          const errorMsg =
            ErrorMessages[ErrorContext.SMART_MEET]?.[_?.status] ??
            t(T.uploadDocFailed);
          logger.error('Error uploading file:', _);
          dispatch(setProgress(null));
          dispatch(setAlert({ type: ALERT.ERROR, msg: errorMsg }));
          navigationBypass(() => navigate(APPS_NAV_LINK.SMART_MEET));
        }
      });
    } catch (error) {
      logger.error('Error uploading meeting audio:', error);
      return null;
    }
  };

  return {
    uploadMeetingAudio
  };
};
